<template>
  <SponsorsDataProvider v-slot="{ sponsorsSortedByOrder }">
    <CategoriesDataProvider v-slot="{ sponsorsCategories }">
      <section
        v-if="shouldRender(sponsorsSortedByOrder)"
        class="sponsors-section px-4"
      >
        <div class="sponsors-section__card">
          <div class="sponsors-section__header">
            <h2 class="sponsors-section__title">{{ sectionTitle }}</h2>
            <button
              v-if="!isTopSponsorsOnly"
              type="button"
              class="sponsors-section__count"
              :aria-label="
                $tc(
                  'Views.Lobby.SponsorsMarquee.viewAllSponsorsAria',
                  sponsorsSortedByOrder.length,
                  {
                    n: sponsorsSortedByOrder.length
                  }
                )
              "
              @click="showAllSponsors"
            >
              {{ $t('Views.Lobby.SponsorsMarquee.viewAllSponsors') }}
            </button>
          </div>

          <!-- The catch-all "Sponsors" section renders flat at the top
               (no pill) and shows every sponsor, exactly like admin.
               Categories the admin defined render below as labelled
               groups. Entirely admin-driven — no auto-typing. -->
          <div
            v-if="leadSponsors(sponsorsSortedByOrder).length"
            class="sponsors-row"
          >
            <button
              v-for="sponsor in leadSponsors(sponsorsSortedByOrder)"
              :key="sponsor.id || sponsor.Id"
              type="button"
              class="sponsors-row__item"
              @click="onSponsorClick(sponsor)"
            >
              <img
                v-if="sponsor.logoUrl"
                :src="sponsor.logoUrl"
                :alt="
                  sponsor.name ||
                  sponsor.companyName ||
                  $t('Views.Lobby.SponsorsMarquee.sponsor')
                "
                class="sponsors-row__logo"
              />
              <span v-else class="sponsors-row__name">
                {{
                  sponsor.name ||
                  sponsor.companyName ||
                  $t('Views.Lobby.SponsorsMarquee.sponsor')
                }}
              </span>
            </button>
          </div>

          <div
            v-if="definedGroups(sponsorsSortedByOrder).length"
            class="sponsors-section__groups"
          >
            <div
              v-for="(group, gi) in definedGroups(sponsorsSortedByOrder)"
              :key="group.id"
              class="sponsor-group"
              :class="`sponsor-group--theme-${gi % 6}`"
            >
              <span class="sponsor-group__pill">{{ group.label }}</span>
              <div class="sponsor-group__cards">
                <button
                  v-for="sponsor in group.sponsors"
                  :key="sponsor.id || sponsor.Id"
                  type="button"
                  class="sponsor-tile"
                  @click="onSponsorClick(sponsor)"
                >
                  <img
                    v-if="sponsor.logoUrl"
                    :src="sponsor.logoUrl"
                    :alt="
                      sponsor.name ||
                      sponsor.companyName ||
                      $t('Views.Lobby.SponsorsMarquee.sponsor')
                    "
                    class="sponsor-tile__logo"
                  />
                  <span v-else class="sponsor-tile__name">
                    {{
                      sponsor.name ||
                      sponsor.companyName ||
                      $t('Views.Lobby.SponsorsMarquee.sponsor')
                    }}
                  </span>
                </button>
              </div>
            </div>
          </div>

          <!-- One callout per admin "Top sponsors" module, in the order
               the admin arranged them. Each keeps its own label, its own
               sponsor order and its own "Size" slider value (exposed to
               the CSS below as `--top-sponsor-scale`). -->
          <div
            v-for="group in topSponsorGroups(sponsorsSortedByOrder)"
            :key="group.key"
            class="top-sponsors"
            :style="{ '--top-sponsor-scale': group.scale }"
          >
            <span v-if="!isTopSponsorsOnly" class="top-sponsors__pill">
              {{ group.label }}
            </span>
            <div class="top-sponsors__cards">
              <button
                v-for="sponsor in group.sponsors"
                :key="`${group.key}-${sponsor.id || sponsor.Id}`"
                type="button"
                class="top-sponsors__card"
                @click="onSponsorClick(sponsor)"
              >
                <img
                  v-if="sponsor.logoUrl"
                  :src="sponsor.logoUrl"
                  :alt="
                    sponsor.name ||
                    sponsor.companyName ||
                    $tc('Views.Lobby.SponsorsMarquee.topSponsors', 1)
                  "
                  class="top-sponsors__logo"
                />
                <span v-else class="top-sponsors__name">
                  {{
                    sponsor.name ||
                    sponsor.companyName ||
                    $tc('Views.Lobby.SponsorsMarquee.topSponsors', 1)
                  }}
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>
    </CategoriesDataProvider>
  </SponsorsDataProvider>
</template>

<script>
import { mapGetters } from 'vuex';
import SponsorInfoModal from '@/components/SponsorInfoModal';
import SponsorsListModal from '@/components/SponsorsListModal';
import { moduleType } from '@/constants/lobby-page-constants';
import sponsorCategories from '@/mixins/sponsorCategories';
import {
  resolveTopSponsors,
  topSponsorIdsOf,
  topSponsorScale
} from '@/helpers/top-sponsors';

export default {
  name: 'SponsorsMarquee',
  mixins: [sponsorCategories],
  props: {
    // The admin's heading for the sponsor section (lobby builder
    // "Label", e.g. "MNO Typed Sponsors"). Falls back to "Sponsors".
    label: {
      type: String,
      default: ''
    },
    // Categories the admin defined as sponsor sections in the lobby
    // builder ("Select category"), in admin order. Each becomes a
    // labelled group on the platform. Empty → no grouping (flat list).
    categoryFilters: {
      type: Array,
      default: () => []
    },
    // Whether the admin has a catch-all (no-category) sponsors section.
    // When true, the flat "Sponsors" list at the top shows every sponsor
    // (mirroring admin); when false it's hidden and only the category
    // groups show.
    showUncategorized: {
      type: Boolean,
      default: true
    },
    // Whether to render the "Top Sponsors" callout.
    showTopSponsors: {
      type: Boolean,
      default: true
    },
    // A single admin "Top sponsors" lobby element. When set, the section
    // renders only that module — titled with its label, at its own spot in
    // the lobby order — and none of the regular sponsor lists.
    topSponsorElement: {
      type: Object,
      default: null
    }
  },
  computed: {
    ...mapGetters('CurrentEvent', ['lobbyElements', 'features']),
    // Whether the sponsor search / category filter UI should be shown —
    // mirrors LobbySponsors: the per-section "Filters" toggle
    // (options.isSearchEnabled) AND the global sponsorFilter feature.
    filtersEnabled() {
      const f = this.features || {};
      if (f.sponsorFilter === false) return false;
      const els = Array.isArray(this.lobbyElements) ? this.lobbyElements : [];
      return els.some(
        (x) =>
          x &&
          (x.id === 'sponsors' || x.type === moduleType.SPONSOR) &&
          x.options &&
          x.options.isSearchEnabled
      );
    },
    // Every admin "Top sponsors" lobby element, in lobby order. The
    // builder generates a random id for each one (and allows more than
    // one), so the numeric module type is the reliable match; the legacy
    // string ids are kept for events saved by older builders.
    topSponsorElements() {
      if (this.isTopSponsorsOnly) return [this.topSponsorElement];
      if (!Array.isArray(this.lobbyElements)) return [];
      return this.lobbyElements.filter(
        (x) =>
          x &&
          (x.type === moduleType.TOP_SPONSORS ||
            x.id === 'topSponsors' ||
            x.id === 'topsponsors')
      );
    },
    isTopSponsorsOnly() {
      return !!this.topSponsorElement;
    },
    sectionTitle() {
      if (!this.isTopSponsorsOnly)
        return this.label || this.$t('Views.Lobby.SponsorsMarquee.sponsors');
      const label = this.topSponsorElement.options?.label;
      return (
        (label && String(label).trim()) ||
        this.$tc('Views.Lobby.SponsorsMarquee.topSponsors', 2)
      );
    },
    // Whether the admin configured any top sponsors at all — cheap to
    // evaluate before the sponsor list has loaded.
    hasTopSponsorConfig() {
      return this.topSponsorElements.some(
        (el) => topSponsorIdsOf((el && el.options) || {}).length
      );
    }
  },
  methods: {
    // Render the section only when there's something to show.
    shouldRender(list) {
      if (this.leadSponsors(list).length) return true;
      if (this.definedGroups(list).length) return true;
      if (this.topSponsorGroups(list).length) return true;
      return false;
    },
    // One render-ready callout per Top sponsors module: the admin's
    // label, their "Size" slider as a scale factor, and their sponsors
    // in the exact order they dragged them. Empty modules are dropped.
    topSponsorGroups(list) {
      if (!this.showTopSponsors || !this.hasTopSponsorConfig) return [];
      return this.topSponsorElements
        .map((el, index) => {
          const options = (el && el.options) || {};
          const sponsors = resolveTopSponsors(options, list);
          const label = options.label && String(options.label).trim();
          return {
            key: `${(el && el.id) || 'top-sponsors'}-${index}`,
            label:
              label ||
              this.$tc(
                'Views.Lobby.SponsorsMarquee.topSponsors',
                sponsors.length
              ),
            scale: topSponsorScale(options.size),
            sponsors
          };
        })
        .filter((group) => group.sponsors.length);
    },
    // The admin's defined categories, normalised and de-duplicated.
    normalizedCategoryFilters() {
      const out = [];
      const seen = new Set();
      (Array.isArray(this.categoryFilters) ? this.categoryFilters : []).forEach(
        (cf) => {
          if (cf === null || cf === undefined || String(cf).trim() === '') {
            return;
          }
          const key = String(cf).trim().toLowerCase();
          if (seen.has(key)) return;
          seen.add(key);
          out.push(cf);
        }
      );
      return out;
    },
    // The flat "Sponsors" section at the top. It mirrors the admin's
    // catch-all (no-category) section, which shows EVERY sponsor — so a
    // sponsor that's also in a category still appears here, exactly as
    // admin shows it. Hidden only when the admin has no catch-all
    // section (i.e. every sponsor section has a category selected).
    leadSponsors(list) {
      if (this.isTopSponsorsOnly) return [];
      const all = Array.isArray(list) ? list : [];
      if (!this.normalizedCategoryFilters().length) return all;
      return this.showUncategorized ? all : [];
    },
    // One labelled group per admin-defined category (skips empties).
    definedGroups(list) {
      if (this.isTopSponsorsOnly) return [];
      const all = Array.isArray(list) ? list : [];
      return this.normalizedCategoryFilters()
        .map((cf) => ({
          id: String(cf).trim().toLowerCase(),
          label: this.categoryLabel(cf),
          sponsors: all.filter((s) => this.sponsorInCategory(s, cf))
        }))
        .filter((g) => g.sponsors.length);
    },
    // Resolve a category's display label from its selected value (id /
    // value / label). Falls back to the raw value when it's a text label.
    categoryLabel(value) {
      const target = String(value).trim().toLowerCase();
      const match = this._spAllCats().find((c) => {
        if (!c) return false;
        const id = String(this._spCatId(c)).trim().toLowerCase();
        const label = String(this._spCatLabel(c)).trim().toLowerCase();
        const val =
          c.value !== undefined && c.value !== null
            ? String(c.value).trim().toLowerCase()
            : '';
        return id === target || label === target || val === target;
      });
      if (match && this._spCatLabel(match)) return this._spCatLabel(match);
      const raw = String(value).trim();
      return raw && !/^\d+$/.test(raw)
        ? raw
        : this.$t('Views.Lobby.SponsorsMarquee.sponsors');
    },
    onSponsorClick(sponsor) {
      if (!sponsor) return;
      document.body.classList.add('overflow-hidden');
      this.$modal.show(
        SponsorInfoModal,
        { sponsor },
        {
          adaptive: true,
          minWidth: 320,
          maxWidth: 600,
          width: '90%',
          height: 'auto'
        }
      );
    },
    showAllSponsors() {
      document.body.classList.add('overflow-hidden');
      this.$modal.show(
        SponsorsListModal,
        { showFilters: this.filtersEnabled },
        {
          clickToClose: true,
          adaptive: true,
          minWidth: 300,
          maxWidth: 560,
          width: '92%',
          height: 'auto'
        },
        { closed: () => document.body.classList.remove('overflow-hidden') }
      );
    }
  }
};
</script>

<style scoped lang="scss">
.sponsors-section {
  font-family: 'Poppins', sans-serif;
  background: transparent;
  padding-top: 32px;
  padding-bottom: 32px;

  @media (max-width: 640px) {
    padding-top: 20px;
    padding-bottom: 20px;
  }

  // Outer card — same chrome the FAQ, Speakers and JoinCommunity
  // sections use so all four lobby blocks read as a uniform set.
  &__card {
    background: #ffffff;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    border: 1px solid #e5e7eb;

    @media (max-width: 640px) {
      padding: 18px 16px;
    }
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-bottom: 24px;
  }

  &__title {
    font-size: 20px;
    font-weight: 700;
    color: #111827;
    margin: 0;
  }

  // Same soft pill used by Speakers / JoinCommunity for "view all"
  // controls so every section's secondary action feels consistent.
  &__count {
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    background: #f3f4f6;
    padding: 4px 12px;
    border-radius: 9999px;
    border: none;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s, color 0.15s;
    font-family: 'Poppins', sans-serif;

    &:hover,
    &:focus-visible {
      background: #e5e7eb;
      color: #111827;
      outline: none;
    }
  }
}

// ── Default uncategorized row ──────────────────────────────────────
// Logos sit on the page background (no card chrome) — matches the
// minimal layout in the design reference.
.sponsors-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 24px 32px;
  margin-bottom: 28px;

  @media (max-width: 640px) {
    gap: 18px 24px;
    margin-bottom: 22px;
  }

  &__item {
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    transition: opacity 0.15s, transform 0.15s;

    &:hover {
      opacity: 1;
      transform: translateY(-1px);
    }
  }

  &__logo {
    max-height: 56px;
    max-width: 160px;
    object-fit: contain;
    filter: grayscale(15%);
    opacity: 0.95;
    transition: filter 0.15s, opacity 0.15s;

    &:hover {
      filter: grayscale(0);
      opacity: 1;
    }
  }

  &__name {
    font-size: 15px;
    font-weight: 600;
    color: #374151;
  }
}

// ── Top Sponsor callout ────────────────────────────────────────────
// Tile geometry scales with the admin's "Size" slider (60–100%),
// supplied per callout as `--top-sponsor-scale`. Mirrors the
// `--card-size` scaling of the `tappin-top-sponsors` web component so
// the lobby builder preview and the platform agree.
.top-sponsors {
  --top-sponsor-scale: 1;

  & + & {
    margin-top: 22px;
  }

  &__pill {
    display: inline-flex;
    align-items: center;
    padding: 5px 16px;
    border-radius: 9999px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    margin-bottom: 14px;
    // Transparent-green pill: soft green tint on the chip, deeper
    // green text for the label.
    background: rgba(22, 163, 74, 0.12);
    color: #15803d;
  }

  &__cards {
    display: grid;
    grid-template-columns: repeat(
      auto-fit,
      minmax(calc(260px * var(--top-sponsor-scale)), 1fr)
    );
    gap: 14px;

    @media (max-width: 480px) {
      grid-template-columns: repeat(
        auto-fit,
        minmax(calc(180px * var(--top-sponsor-scale)), 1fr)
      );
      gap: 10px;
    }
  }

  &__card {
    display: flex;
    align-items: center;
    justify-content: center;
    height: calc(110px * var(--top-sponsor-scale));
    padding: 14px 18px;
    border: 1px solid rgba(0, 0, 0, 0.04);
    border-radius: 14px;
    cursor: pointer;
    transition: transform 0.15s, box-shadow 0.15s;
    font-family: 'Poppins', sans-serif;
    // Soft teal→lavender gradient matching the highlighted row in the
    // design reference. Subtle but distinct from the default tiles.
    background: linear-gradient(135deg, #e0f5f1 0%, #efe4f9 100%);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 14px rgba(0, 0, 0, 0.08);
    }

    @media (max-width: 480px) {
      height: calc(90px * var(--top-sponsor-scale));
    }
  }

  &__logo {
    max-height: calc(72px * var(--top-sponsor-scale));
    max-width: 100%;
    object-fit: contain;
  }

  &__name {
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    text-align: center;
  }
}

// ── Categorized layout (kept for events that admin tagged) ─────────
.sponsors-section__groups {
  margin-bottom: 28px;
}

.sponsor-group {
  position: relative;

  & + & {
    margin-top: 22px;
  }

  &__pill {
    display: inline-flex;
    align-items: center;
    padding: 4px 14px;
    border-radius: 9999px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #ffffff;
    margin-bottom: 14px;
  }

  &__cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 14px;

    @media (max-width: 480px) {
      grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
      gap: 10px;
    }
  }

  &__divider {
    margin-top: 22px;
    border-top: 1px solid #f1f2f4;
  }

  &--theme-0 {
    .sponsor-group__pill {
      background: linear-gradient(135deg, #6d28d9, #a855f7);
    }
    .sponsor-tile {
      background: linear-gradient(135deg, #f3eaff, #eef0fb);
    }
  }
  &--theme-1 {
    .sponsor-group__pill {
      background: linear-gradient(135deg, #d97706, #f59e0b);
    }
    .sponsor-tile {
      background: #fef7d6;
    }
  }
  &--theme-2 {
    .sponsor-group__pill {
      background: #4b5563;
    }
    .sponsor-tile {
      background: #f3f4f6;
    }
  }
  &--theme-3 {
    .sponsor-group__pill {
      background: linear-gradient(135deg, #b45309, #ea580c);
    }
    .sponsor-tile {
      background: #fff1e6;
    }
  }
  &--theme-4 {
    .sponsor-group__pill {
      background: linear-gradient(135deg, #1d4ed8, #3b82f6);
    }
    .sponsor-tile {
      background: #e6efff;
    }
  }
  &--theme-5 {
    // The teal slot of the rotation follows the event's brand colour.
    .sponsor-group__pill {
      background: var(--t-btn, #047873);
      color: var(--t-btn-on, #ffffff);
    }
    .sponsor-tile {
      background: var(--brand-soft, #e6f5f4);
    }
  }
}

.sponsor-tile {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 96px;
  padding: 12px 16px;
  border: 1px solid rgba(0, 0, 0, 0.04);
  border-radius: 14px;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
  font-family: 'Poppins', sans-serif;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 14px rgba(0, 0, 0, 0.08);
  }

  &__logo {
    max-height: 64px;
    max-width: 100%;
    object-fit: contain;
  }

  &__name {
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    text-align: center;
  }

  @media (max-width: 480px) {
    height: 80px;
  }
}
</style>
